import React from 'react';
import { Typography, Tag, Card, Row, Col, Input, CheckboxGroup, Button, Space } from '@douyinfe/semi-ui';
import { IconSearch, IconGlobeStrokeStroked, IconUserStroked, IconMapPinStroked, IconCreditCardStroked, IconHeartStroked, IconIdCardStroked, IconSend, IconServer } from '@douyinfe/semi-icons';

const { Text } = Typography;

const EMPTY_REPORT = {
  meta: {
    target: '-',
    generated: '-',
    confidence: '-',
    sources: '-',
    risk: '-'
  },
  basic_info: {
    name: '-',
    age: '-',
    gender: '-',
    citizenship: '-',
    location: '-',
    education: '-',
    occupation: '-',
    marital_status: '-'
  },
  geolocation_history: [
    { date: '-', location: '-', source: '-' }
  ],
  social_media: [
    { platform: '-', username: '-', status: '-' }
  ],
  financial_info: {
    bank_accounts: '-',
    credit_score: '-',
    tax_compliance: '-',
    main_bank: '-',
    last_activity: '-'
  },
  registry_info: {
    marital_status: '-',
    spouse: '-',
    children: '-',
    relatives: '-'
  },
  legal_info: {
    criminal_record: '-',
    wanted: '-',
    offenses: '-',
    legal_actions: '-'
  },
  additional_data: {
    emails_found: '-',
    phone_numbers: '-',
    domains: '-',
    data_leaks: '-',
    last_seen: '-'
  }
};

const fetchMockOsintReport = async (query) => {
  await new Promise(resolve => setTimeout(resolve, 1200));

  return {
    success: true,
    query,
    report: {
      meta: {
        target: query || 'John Doe',
        generated: '2025-05-25 23:47:12',
        confidence: '82%',
        sources: 14,
        risk: 'Medium'
      },
      basic_info: {
        name: 'John Doe',
        age: 34,
        gender: 'Male',
        citizenship: 'United States',
        location: 'New York, USA',
        education: 'B.Sc. Computer Science',
        occupation: 'Software Developer',
        marital_status: 'Married'
      },
      geolocation_history: [
        { date: '2025-05-20 14:22', location: 'New York, USA', source: 'IP Geolocation' },
        { date: '2025-05-18 09:11', location: 'Boston, USA', source: 'WiFi Metadata' },
        { date: '2025-05-15 21:37', location: 'Chicago, USA', source: 'IP Geolocation' },
        { date: '2025-05-10 16:05', location: 'Miami, USA', source: 'Mobile Network' }
      ],
      social_media: [
        { platform: 'Twitter', username: '@johndoe', status: 'Active' },
        { platform: 'LinkedIn', username: '/in/johndoe', status: 'Active' },
        { platform: 'GitHub', username: 'johndoe-dev', status: 'Active' },
        { platform: 'Reddit', username: 'johndoe_1987', status: 'Inactive' },
        { platform: 'Instagram', username: '@j.doe', status: 'Active' }
      ],
      financial_info: {
        bank_accounts: 2,
        credit_score: '742 / Good',
        tax_compliance: 'Compliant',
        main_bank: 'Chase Bank',
        last_activity: '2025-05-23'
      },
      registry_info: {
        marital_status: 'Married',
        spouse: 'Jane Doe',
        children: 1,
        relatives: '3 Registered'
      },
      legal_info: {
        criminal_record: 'None Found',
        wanted: 'No',
        offenses: 'None',
        legal_actions: 'None'
      },
      additional_data: {
        emails_found: 3,
        phone_numbers: 2,
        domains: 4,
        data_leaks: 0,
        last_seen: '2 days ago'
      }
    }
  };
};

const OptionTiles = ({ label, value, options, onChange }) => (
  <div className="osint-option-block">
    <Text className="osint-form-label">{label}</Text>

    <div className="osint-option-grid">
      {options.map((option) => (
        <Button
          key={option}
          className={
            value === option
              ? 'osint-option-tile active'
              : 'osint-option-tile'
          }
          onClick={() => onChange(option)}
        >
          {option}
        </Button>
      ))}
    </div>
  </div>
);

const HistoryData = ({ items, onSelect }) => (
  <Card className="info-card osint-panel osint-history-card" title="History Data">
    <Space vertical align="start" style={{ width: '100%' }}>
      {items.map((item) => (
        <div
          key={item.id}
          className="osint-history-item"
          onClick={() => onSelect(item)}
        >
          <strong>{item.target}</strong>
          <span>{item.type} · {item.time}</span>
          <Tag className="tag-custom tag-green">{item.status}</Tag>
        </div>
      ))}
    </Space>
  </Card>
);

const MetricLine = ({ label, value }) => (
  <div className="osint-additional-item">
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const MetricCard = ({ label, value, type }) => (
  <div className={`osint-metric osint-metric-${type || 'default'}`}>
    <span>{label}</span>
    <strong>{value}</strong>
  </div>
);

const ReportField = ({ label, value, positive }) => (
  <div className="osint-flat-field">
    <span className="osint-flat-label">{label}</span>
    <span className={positive ? 'osint-flat-value positive' : 'osint-flat-value'}>
      {value}
    </span>
  </div>
);

const MiniReportCard = ({ title, icon, children, accent, className = '' }) => (
  <Card
    className={`osint-mini-card ${accent ? `osint-card-${accent}` : ''} ${className}`}
    title={
      <Typography.Text className="osint-mini-title">
        {icon}
        {title}
      </Typography.Text>
    }
  >
    <div className="osint-mini-card-content">
      {children}
    </div>
  </Card>
);

const IntelReportSemi = ({ data }) => {
  if (!data) {
    return (
      <Card className="info-card osint-report-empty">
        <Typography.Title heading={5} className="osint-report-heading">
          No Active Report
        </Typography.Title>
        <Text className="textBlock">
          Run a demo scan to generate intelligence dashboard.
        </Text>
      </Card>
    );
  }

  const { meta } = data;

  return (
    <Card className="info-card osint-report-card-main">
      <Row gutter={[16, 16]}>

        <Col span={24}>
          <div className="intel-report-header">
            <div>
              <Typography.Title heading={4} className="osint-report-heading">
                <IconUserStroked /> Intelligence Report:
              </Typography.Title>
              <Text className="textBlock">
                Target: {meta.target} · Generated: {meta.generated}
              </Text>
            </div>

            <div className="intel-metrics">
              <MetricCard label="Confidence" value={meta.confidence} type="cyan" />
              <MetricCard label="Sources" value={meta.sources} type="purple" />
              <MetricCard label="Risk Level" value={meta.risk} type="amber" />
            </div>
          </div>
        </Col>

        <Col xs={24} lg={12} xl={8}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <MiniReportCard
                title="Basic Information"
                icon={<IconUserStroked />}
                accent="wide"
                className="osint-bg-fingerprint"
              >
                <div className="osint-card-body">
                  <ReportField label="Name" value={data.basic_info.name} />
                  <ReportField label="Age" value={data.basic_info.age} />
                  <ReportField label="Gender" value={data.basic_info.gender} />
                  <ReportField label="Citizenship" value={data.basic_info.citizenship} />
                  <ReportField label="Location" value={data.basic_info.location} />
                  <ReportField label="Education" value={data.basic_info.education} />
                  <ReportField label="Occupation" value={data.basic_info.occupation} />
                  <ReportField label="Marital Status" value={data.basic_info.marital_status} />
                </div>
              </MiniReportCard>
            </Col>

            <Col span={24}>
              <MiniReportCard
                title="Financial Information"
                icon={<IconCreditCardStroked />}
                accent="cyan"
                className="osint-bg-bank"
              >
                <div className="osint-card-body">
                  <ReportField label="Bank Accounts" value={data.financial_info.bank_accounts} />
                  <ReportField label="Credit Score" value={data.financial_info.credit_score} />
                  <ReportField label="Tax Compliance" value={data.financial_info.tax_compliance} />
                  <ReportField label="Main Bank" value={data.financial_info.main_bank} />
                  <ReportField label="Last Activity" value={data.financial_info.last_activity} />
                </div>
                <div className="osint-card-footer">
                  <Button className="osint-card-action">View Summary →</Button>
                </div>
              </MiniReportCard>
            </Col>
          </Row>
        </Col>

        <Col xs={24} lg={12} xl={8}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <MiniReportCard title="Social Media Accounts" icon={<IconGlobeStrokeStroked />}>
                <div className="osint-card-body">
                  {data.social_media.map((item) => (
                    <ReportField
                      key={item.platform}
                      label={item.platform}
                      value={`${item.username} · ${item.status}`}
                      positive={item.status === 'Active'}
                    />
                  ))}
                </div>
                <div className="osint-card-footer">
                  <Button className="osint-card-action">View All Accounts →</Button>
                </div>
              </MiniReportCard>
            </Col>

            <Col span={24}>
              <MiniReportCard title="Geolocation History" className="osint-bg-geo" icon={<IconMapPinStroked />}>
                <div className="osint-card-body">
                  {data.geolocation_history.map((item) => (
                    <ReportField
                      key={`${item.date}-${item.location}`}
                      label={item.date}
                      value={`${item.location} · ${item.source}`}
                    />
                  ))}
                </div>
                <div className="osint-card-footer">
                  <Button className="osint-card-action">View Full Timeline →</Button>
                </div>
              </MiniReportCard>
            </Col>
          </Row>
        </Col>

        <Col xs={24} xl={8}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <MiniReportCard title="Registry Info" icon={<IconHeartStroked />} accent="purple" className="osint-card-fixed">
                <div className="osint-card-body">
                  <ReportField label="Marital Status" value={data.registry_info.marital_status} />
                  <ReportField label="Spouse" value={data.registry_info.spouse} />
                  <ReportField label="Children" value={data.registry_info.children} />
                  <ReportField label="Relatives" value={data.registry_info.relatives} />
                </div>
                <div className="osint-card-footer">
                  <Button className="osint-card-action">View Details →</Button>
                </div>
              </MiniReportCard>
            </Col>

            <Col span={24}>
              <MiniReportCard title="Criminal & Legal Status" className="osint-bg-legal osint-card-fixed" icon={<IconIdCardStroked />} accent="danger">
                <div className="osint-card-body">
                  <ReportField label="Criminal Record" value={data.legal_info.criminal_record} positive />
                  <ReportField label="Wanted" value={data.legal_info.wanted} positive />
                  <ReportField label="Offenses" value={data.legal_info.offenses} />
                  <ReportField label="Legal Actions" value={data.legal_info.legal_actions} />
                </div>
                <div className="osint-card-footer">
                  <Button className="osint-card-action">View Full Report →</Button>
                </div>
              </MiniReportCard>
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <MiniReportCard title="Additional Data" icon={<IconServer />} accent="purple" className="osint-additional-card">
            <div className="osint-additional-grid">
              <MetricLine label="Emails Found" value={data.additional_data.emails_found} />
              <MetricLine label="Phone Numbers" value={data.additional_data.phone_numbers} />
              <MetricLine label="Domains" value={data.additional_data.domains} />
              <MetricLine label="Data Leaks" value={data.additional_data.data_leaks} />
              <MetricLine label="Last Seen" value={data.additional_data.last_seen} />
            </div>

            <div className="osint-card-footer">
              <Button className="osint-card-action">View All Data Points →</Button>
            </div>
          </MiniReportCard>
        </Col>

      </Row>
    </Card>
  );
};

class OSINTPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      report: null,
      searchParams: {
        target: '',
        mode: 'Profile',
        depth: 'Standard',
        output: 'Intel Report',
        region: 'Global',
        include: ['Social Media', 'Breaches', 'Domains', 'Leaks Metadata']
      },
      history: [
        {
          id: 1,
          type: 'Profile',
          target: 'Vasya',
          status: 'Completed',
          time: '23:47:12'
        }
      ]
    };
  }

  handleParamChange = (key, value) => {
    this.setState(prev => ({
      searchParams: {
        ...prev.searchParams,
        [key]: value
      }
    }));
  };

  handleMessageSend = async (value) => {
    const target = value || this.state.searchParams.target || '-';

    const response = await fetchMockOsintReport(target);

    const historyItem = {
      id: Date.now(),
      type: this.state.searchParams.mode,
      target,
      status: 'Completed',
      time: new Date().toLocaleTimeString()
    };

    this.setState(prev => ({
      report: response.report,
      history: [historyItem, ...prev.history]
    }));
  };

  handleHistorySelect = async (item) => {
    const response = await fetchMockOsintReport(item.target);

    this.setState({
      report: response.report
    });
  };

  render() {
    const { searchParams, report } = this.state;

    return (
      <div className="grid-container osint-page">
        <div className="page-header">
          <Text className="page-title">OSINT</Text>
        </div>

        <Row gutter={[16, 16]} className="osint-dashboard-row">
          <Col xs={24} xl={7} className="osint-left">
          <Card className="info-card osint-welcome-console">
            <div className="osint-welcome-content">
              <Typography.Title heading={5} className="osint-report-heading">
                OSINT Control Panel
              </Typography.Title>
              <Text className="textBlock">
                Welcome to the intelligence workspace. Configure scan parameters, run analysis, and review structured reports.
              </Text>
            </div>

            <img
              src="/assets/osi_face.png"
              alt=""
              className="osint-welcome-art"
            />
          </Card>
            <Card className="info-card osint-panel" title="Search Parameters">
              <Row gutter={[12, 12]}>
                <Col span={24}>
                  <Text className="osint-form-label">Target</Text>
                  <Input
                    prefix={<IconSearch />}
                    value={searchParams.target}
                    onChange={(v) => this.handleParamChange('target', v)}
                    className="osint-input"
                    placeholder="Type your command..."
                  />
                </Col>

                <Col xs={24} md={12}>
                  <OptionTiles
                    label="Mode"
                    value={searchParams.mode}
                    options={['Profile', 'Username', 'Phone', 'E-mail']}
                    onChange={(v) => this.handleParamChange('mode', v)}
                  />

                </Col>

                <Col xs={24} md={12}>
                  <OptionTiles
                    label="Depth"
                    value={searchParams.depth}
                    options={['Quick', 'Standard', 'Deep']}
                    onChange={(v) => this.handleParamChange('depth', v)}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <OptionTiles
                    label="Output"
                    value={searchParams.output}
                    options={['Report', 'Summary', 'Timeline']}
                    onChange={(v) => this.handleParamChange('output', v)}
                  />
                </Col>

                <Col xs={24} md={12}>
                  <OptionTiles
                    label="Region"
                    value={searchParams.region}
                    options={['Global', 'US', 'EU', 'RU', 'UK']}
                    onChange={(v) => this.handleParamChange('region', v)}
                  />
                </Col>

                <Col span={24}>
                  <Text className="card-paragraph"><strong>Include</strong></Text>
                  <CheckboxGroup
                    value={searchParams.include}
                    onChange={(v) => this.handleParamChange('include', v)}
                    options={[
                      'Social Media',
                      'Breaches',
                      'Domains',
                      'Leaks Metadata'
                    ]}
                    className="ui-checkbox-group osint-checkboxes"
                  />
                </Col>

                <Col span={24}>
                  <Button
                    className="button-custom osint-run-button"
                    icon={<IconSend />}
                    onClick={() => this.handleMessageSend(searchParams.target || 'Run demo profile scan')}
                  >
                    Run Scan
                  </Button>
                </Col>
              </Row>
            </Card>

            <HistoryData
              items={this.state.history}
              onSelect={this.handleHistorySelect}
            />
          </Col>

          <Col xs={24} xl={17} className="osint-right">
            <IntelReportSemi data={report || EMPTY_REPORT} />
          </Col>
        </Row>
      </div>
    );
  }
}

export default OSINTPage;